import { v4 as uuidv4 } from 'uuid';
import {
    getAllElementsRecursively,
    ElementTypes,
    TextElement,
    TextStyle,
    Template,
    ControllableElement,
} from '@bynder-studio/render-core';
import { collectStyleIds } from 'packages/pages/editor/RightSideMenu/Shots/Text/TextStyles/utils';

export type MatchList = { [key: TextStyle['uuid']]: TextStyle['uuid'] };

const collectTextStyles = (selectedElements: ControllableElement[], currentStyles: TextStyle[]) => {
    const textElements = getAllElementsRecursively(selectedElements).filter(
        (element) => element instanceof TextElement,
    );

    return [
        ...new Set(
            (textElements as TextElement[]).flatMap((element) =>
                [...collectStyleIds(element as TextElement)].filter((style) => !!style),
            ),
        ),
    ]
        .map((styleId) => currentStyles.find(({ uuid }) => uuid === styleId))
        .filter((style) => !!style) as TextStyle[];
};

export const convertTextStyles = (
    selectedElements: ControllableElement[],
    currentStyles: TextStyle[],
): [MatchList, TextStyle[]] => {
    const idsMatchList: MatchList = {};

    const textStyles = collectTextStyles(selectedElements, currentStyles).map((style) => {
        const { uuid, ...styleProps } = style;
        const newUuid = uuidv4();
        idsMatchList[uuid] = newUuid;

        return { ...styleProps, uuid: newUuid };
    });

    return [idsMatchList, textStyles as TextStyle[]];
};

export const replaceStyleIds = (
    formattedText: { runs: TextElement['formattedText']['runs'] },
    matchList: MatchList,
) => {
    return formattedText.runs.map((run) => {
        if (run.styleId && matchList[run.styleId]) {
            run.styleId = matchList[run.styleId];
        }

        return run;
    });
};

export const replaceDuplicatedIds = (elements: Template['elements'], matchList: MatchList) => {
    Object.values(elements).forEach((element) => {
        if (element?.children) {
            return replaceDuplicatedIds(element.children, matchList);
        }

        if (element?.type === 'TEXT') {
            const styleId = matchList[element.properties.styleId];

            if (styleId) {
                element.properties.styleId = styleId;
            }

            element.properties.value.runs = replaceStyleIds(element.properties.value, matchList);
        }
    });

    return elements;
};

export type Node<T> = T & { children?: Record<string, Node<T>> };

export const getAllNodesRecursively = <T extends object>(node: Node<T>) => {
    const result: Node<T>[] = [node];

    if (!node?.children) {
        return result;
    }

    Object.values(node.children).forEach((child) => {
        result.push(...getAllNodesRecursively(child));
    });

    return result;
};

export const getContentPropertiesFromElement = (creativeModel: any, elements: any, matchList: MatchList) => {
    if (!creativeModel) {
        return [];
    }

    const allContentProperties = creativeModel.getContentPropertiesManager().toObject();
    const ids = new Set(elements.map((element) => element.contentPropertyUuid as string).filter(Boolean));

    const contentProperties = allContentProperties
        .filter((item) => ids.has(item.uuid))
        .map((item) => {
            const uuid = uuidv4();

            elements.forEach((element) => {
                if (element.contentPropertyUuid !== item.uuid) {
                    return;
                }

                const el = elements.find(({ id }) => element.id === id);

                if (el) {
                    el.contentPropertyUuid = uuid;
                }
            });

            if (item.type === ElementTypes.TEXT) {
                item.properties.textStyles = (item.properties.textStyles ?? []).reduce((acc, styleId) => {
                    const newVal = matchList[styleId];

                    if (newVal) {
                        acc.push(newVal);
                    }

                    return acc;
                }, [] as string[]);

                item.properties.value?.runs?.forEach((run) => {
                    run.styleId = matchList[run.styleId] ?? null;
                });
            }

            return { ...item, uuid };
        });

    return contentProperties;
};
